import type { ComponentType } from 'react'
import clsx from 'clsx'

import type { ChatStarter } from '../../lib/data'
import type { IconProps } from '../icons'
import { Star } from '../icons'
import { Sparkle } from '../interests/icons'
import { Unavailable } from '../interests/parts'
import { BookOpen, Mic } from './icons'
import { readsLine } from './labels'

// ============================================================================
// THE EMPTY THREAD — LifeOS Chat.dc.html, option 11b, the centre.
//
// README §10: "Centred: a 44px violet sparkle tile, a 300 26px heading 'Ask about
// your own life', a promise ('If something can't be cited, it won't be claimed'),
// and three starter cards, each naming what it would read."
//
// EACH STARTER NAMES WHAT IT WOULD READ, and the number is counted over the
// corpus by the data contract rather than written on the card. That line is the
// screen's whole promise made checkable before you press anything: a starter that
// claimed nine transcripts over a corpus holding four would be the app
// over-promising in the exact place it is establishing that it does not.
//
// PRESSING ONE NOW CARRIES ITS QUESTION, not just its scope. It used to set the
// scope and drop the sentence printed on the card, which made a card showing a
// question into a card that switched a chip. The card hands both to /chat's
// composer; what the composer does with them is decided there, in one place,
// because there is one send path in this app.
// ============================================================================

const GLYPH: Record<ChatStarter['domain'], { Icon: ComponentType<IconProps>; hue: string }> = {
  school: { Icon: Mic, hue: 'text-info' },
  interests: { Icon: Star, hue: 'text-chat' },
  journal: { Icon: BookOpen, hue: 'text-starter-journal' },
}

export function EmptyThread({
  starters,
  isPending,
  error,
  onStart,
}: {
  starters: ChatStarter[] | undefined
  isPending: boolean
  error: unknown
  onStart: (starter: ChatStarter) => void
}) {
  return (
    <div className="flex min-h-0 flex-1 flex-col items-center justify-center gap-6 px-[30px]">
      <div className="flex flex-col items-center gap-[11px]">
        <span
          className="flex size-[44px] items-center justify-center rounded-card border border-accent-line bg-accent-fill text-chat"
          aria-hidden
        >
          <Sparkle size={22} strokeWidth={1.7} />
        </span>
        <h3 className="text-hero font-light">Ask about your own life</h3>
        <p className="max-w-[520px] text-center text-[13px] font-normal leading-[1.5] text-muted">
          Answers cite what they read — journal entries, lecture transcripts, saved posts. If
          something can’t be cited, it won’t be claimed.
        </p>
      </div>

      <div className="flex w-[640px] flex-col gap-[9px]">
        {error ? (
          <Unavailable error={error} />
        ) : isPending ? (
          <div className="flex flex-col gap-[9px]" aria-hidden>
            {Array.from({ length: 3 }, (_, i) => (
              <div key={i} className="h-[52px] animate-pulse rounded-panel border border-line bg-surface" />
            ))}
            <span className="sr-only">Loading…</span>
          </div>
        ) : (
          (starters ?? []).map((starter) => {
            const { Icon, hue } = GLYPH[starter.domain]
            return (
              <button
                key={starter.id}
                type="button"
                onClick={() => onStart(starter)}
                className={clsx(
                  'mo-press flex items-center gap-[13px] rounded-panel border border-line bg-surface px-[17px] py-[14px] text-left',
                  'transition-colors duration-[var(--dur-hover)] ease-[var(--ease-ink)] hover:bg-surface-2',
                )}
              >
                <Icon size={15} strokeWidth={1.8} className={clsx('flex-none', hue)} />
                <span className="min-w-0 flex-1 text-[13.5px] font-medium leading-[1.35]">
                  {starter.prompt}
                </span>
                <span className="flex-none text-[10.5px] font-normal leading-none text-microcopy">
                  {readsLine(starter.reads)}
                </span>
              </button>
            )
          })
        )}
      </div>
    </div>
  )
}
